// Cookies & Trackers tab — preview, trackers table/filter, scan section tabs.

import { TRACKER_CATEGORIES, TRACKER_CLASSIFICATION_STATUS } from 'src/services/consts/shared.const'

/** Drawer scan-status preview UI variant (derived from {@link SCAN_STATUS}). */
export const SCAN_PREVIEW_VARIANT = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  FAILED: 'failed',
}

export const SCAN_PREVIEW_RING_CLASS_BY_VARIANT = {
  [SCAN_PREVIEW_VARIANT.ACTIVE]: 'drawer-scan-status-ring-scanning',
  [SCAN_PREVIEW_VARIANT.COMPLETED]: 'drawer-scan-status-ring-completed',
  [SCAN_PREVIEW_VARIANT.FAILED]: 'drawer-scan-status-ring-failed',
}

export const SCAN_PREVIEW_TOOLTIP_COLOR_BY_VARIANT = {
  [SCAN_PREVIEW_VARIANT.ACTIVE]: 'text-brand',
  [SCAN_PREVIEW_VARIANT.COMPLETED]: 'text-positive',
  [SCAN_PREVIEW_VARIANT.FAILED]: 'text-warning',
}

export const SCAN_PREVIEW_COPY = {
  INITIATING: 'Initiating scan…',
  FAILED: 'Scan failed',
  READY_STILL_SCANNING: 'Ready to review - scanning continues',
  PREVIEW_RUNNING_TITLE: 'Running a quick preview scan...',
  /** Shown as the subtitle before any progress, and as a secondary note once progress appears. */
  PREVIEW_FULL_SCAN_NOTE: "We'll run a full scan automatically once this is done.",
  PREVIEW_DONE_TITLE: 'Quick results are ready - full scan running',
  PREVIEW_DONE_SUBTITLE:
    "We're now scanning every page on your site for complete results. This can take a few minutes to a few hours, depending on site size.",
}

/** Scan overview card UI state (derived from scan data + busy/fallback flags). */
export const SCAN_STATUS_STATE = {
  NO_SCAN: 'no_scan',
  BUSY_FULL: 'busy_full',
  BUSY_PREVIEW: 'busy_preview',
  BUSY_FALLBACK: 'busy_fallback',
  FAILED: 'failed',
  SUMMARY: 'summary',
  SETTLED: 'settled',
}

const SCAN_STATUS_ICON_BUSY = {
  name: 'refresh-cw',
  wrapClass: 'bg-brand-extra-light',
  iconClass: 'text-brand',
}

const SCAN_STATUS_ICON_DONE = {
  name: 'circle-check',
  wrapClass: 'bg-positive-light',
  iconClass: 'text-positive',
}

export const SCAN_STATUS_ICON_BY_STATE = {
  [SCAN_STATUS_STATE.NO_SCAN]: SCAN_STATUS_ICON_BUSY,
  [SCAN_STATUS_STATE.BUSY_FULL]: SCAN_STATUS_ICON_BUSY,
  [SCAN_STATUS_STATE.BUSY_PREVIEW]: SCAN_STATUS_ICON_BUSY,
  [SCAN_STATUS_STATE.BUSY_FALLBACK]: SCAN_STATUS_ICON_BUSY,
  [SCAN_STATUS_STATE.FAILED]: {
    name: 'alert-triangle',
    wrapClass: 'bg-cta-light',
    iconClass: 'text-cta-dark',
  },
  [SCAN_STATUS_STATE.SUMMARY]: SCAN_STATUS_ICON_DONE,
  [SCAN_STATUS_STATE.SETTLED]: SCAN_STATUS_ICON_DONE,
}

export const SCAN_STATUS_COPY = {
  NO_SCAN_TITLE: 'No scan yet',
  BUSY_FULL_TITLE: 'Scan is currently in progress...',
  BUSY_FULL_SUBTITLE: 'Scanning your website...',
  FAILED_TITLE_FALLBACK: 'Last scan failed to complete',
  COMPLETED_TITLE_FALLBACK: 'Last scan completed successfully',
  NO_TRACKERS: 'No trackers detected.',
}

/** Shared confirm-dialog copy for a site's very first scan (create-banner toggle + manual "Run scan"). */
export const SCAN_FIRST_RUN_NOTE =
  "You'll see your first cookies in about 2 minutes and can start classifying right away - we'll keep scanning in the background until it's complete. Larger sites take a bit longer."

export const TRACKER_TYPES = {
  COOKIE: 'cookie',
  INDEXED_DB: 'indexedDB',
  LOCAL_STORAGE: 'localStorage',
  SESSION_STORAGE: 'sessionStorage',
}

/** All valid tracker type values for URL `?trackerType` validation. */
export const VALID_TRACKER_TYPES = new Set(Object.values(TRACKER_TYPES))

export const TRACKER_TYPE_LABELS = {
  [TRACKER_TYPES.COOKIE]: 'Cookie',
  [TRACKER_TYPES.INDEXED_DB]: 'Indexed DB',
  [TRACKER_TYPES.LOCAL_STORAGE]: 'Local Storage',
  [TRACKER_TYPES.SESSION_STORAGE]: 'Session Storage',
}

/** Lucide icon names for tracker storage types in the trackers table Type column. */
export const TRACKER_TYPE_ICONS = {
  [TRACKER_TYPES.COOKIE]: 'cookie',
  [TRACKER_TYPES.INDEXED_DB]: 'database',
  [TRACKER_TYPES.LOCAL_STORAGE]: 'hard-drive',
  [TRACKER_TYPES.SESSION_STORAGE]: 'clock',
}

/** OverrideScanCategoryPayload keys — API groups all storage types under `webStorage`. */
export const OVERRIDE_SCAN_CATEGORY_KEYS = {
  [TRACKER_TYPES.COOKIE]: 'cookies',
  [TRACKER_TYPES.INDEXED_DB]: 'webStorage',
  [TRACKER_TYPES.LOCAL_STORAGE]: 'webStorage',
  [TRACKER_TYPES.SESSION_STORAGE]: 'webStorage',
}

export const TRACKER_CATEGORY_OPTIONS = [
  { value: TRACKER_CATEGORIES.ANALYTICS, label: 'Analytics' },
  { value: TRACKER_CATEGORIES.MARKETING, label: 'Marketing' },
  { value: TRACKER_CATEGORIES.PREFERENCES, label: 'Preferences' },
  { value: TRACKER_CATEGORIES.NECESSARY, label: 'Necessary' },
]

/** Sidebar "All trackers" value and URL default when no category filter is active. */
export const TRACKER_CATEGORY_ALL_KEY = 'all'

/** Category group key for scan trackers missing a known BE category. */
export const TRACKER_CATEGORY_UNCLASSIFIED_KEY = TRACKER_CLASSIFICATION_STATUS.UNCLASSIFIED

/** Dropdown options for the category override select (includes "Unclassified" to revert). */
export const TRACKER_CATEGORY_DROPDOWN_OPTIONS = [
  ...TRACKER_CATEGORY_OPTIONS,
  { value: TRACKER_CATEGORY_UNCLASSIFIED_KEY, label: 'Unclassified' },
]

/** Tooltip on the category select while showing preview results during PREVIEW_DONE_FULL_RUNNING. */
export const TRACKER_CATEGORY_SELECT_BLOCKED_WHILE_FALLBACK_TOOLTIP =
  "Editing is disabled while the full scan is still running. It'll be available once the full scan completes."

/** Vendor value for CookieArmor's own consent cookie — used to lock its category. */
export const COOKIE_ARMOR_VENDOR = 'CookieArmor'

/** Tooltip on the category select for CookieArmor's own consent cookie. */
export const TRACKER_CATEGORY_SELECT_BLOCKED_SELF_DECLARED_TOOLTIP =
  'This cookie cannot be reclassified. Changing its category would prevent the consent banner from working.'

/** Lucide icon names for the Detected Trackers category sidebar (matches directory-nav pattern). */
export const TRACKER_CATEGORY_FILTER_ICONS = {
  [TRACKER_CATEGORY_ALL_KEY]: 'cookie',
  [TRACKER_CATEGORY_UNCLASSIFIED_KEY]: 'alert-triangle',
  [TRACKER_CATEGORIES.ANALYTICS]: 'chart-column',
  [TRACKER_CATEGORIES.MARKETING]: 'zap',
  [TRACKER_CATEGORIES.PREFERENCES]: 'sliders-horizontal',
  [TRACKER_CATEGORIES.NECESSARY]: 'shield-check',
}

/**
 * All valid sidebar category keys for URL `?category` validation.
 * "All trackers" is explicit here: an absent `?category` means "not chosen yet",
 * which lets the first entry to a banner's scan page auto-pick Unclassified.
 */
export const VALID_TRACKER_CATEGORY_KEYS = new Set([
  TRACKER_CATEGORY_ALL_KEY,
  ...TRACKER_CATEGORY_OPTIONS.map((option) => option.value),
  TRACKER_CATEGORY_UNCLASSIFIED_KEY,
])

/** Sub-tabs within the Cookies & Trackers section (query param `scanTab`). */
export const SCAN_SECTION_TABS = {
  RESULTS: 'results',
  HISTORY: 'history',
}

/** All valid scan tab values for URL `?scanTab` validation. */
export const VALID_SCAN_TABS = new Set(Object.values(SCAN_SECTION_TABS))

/** URL query key for the Cookies & Trackers section tab (handled outside TRACKERS_QUERY_KEYS). */
export const SCAN_TAB_QUERY_KEY = 'scanTab'

export const TRACKERS_TABLE_COLUMN_NAMES = {
  VENDOR: 'vendor',
  NAME: 'name',
  TYPE: 'type',
  CATEGORY: 'category',
}

export const TRACKERS_TABLE_COLUMNS = [
  {
    name: TRACKERS_TABLE_COLUMN_NAMES.VENDOR,
    label: 'Vendor',
    field: 'vendor',
    align: 'left',
    sortable: true,
    classes: 'tracker-vendor main-td',
    headerClasses: 'tracker-vendor main-td',
  },
  {
    name: TRACKERS_TABLE_COLUMN_NAMES.NAME,
    label: 'Key',
    field: 'name',
    align: 'left',
    sortable: true,
    classes: 'tracker-name main-td',
    headerClasses: 'tracker-name main-td',
  },
  {
    name: TRACKERS_TABLE_COLUMN_NAMES.TYPE,
    label: 'Type',
    field: 'type',
    align: 'left',
    sortable: true,
    classes: 'tracker-type',
    headerClasses: 'tracker-type',
  },
  {
    name: TRACKERS_TABLE_COLUMN_NAMES.CATEGORY,
    label: 'Category',
    field: 'category',
    align: 'left',
    sortable: true,
    classes: 'tracker-category xl-td compact-lg-td',
    headerClasses: 'tracker-category xl-td compact-lg-td',
  },
]

/** Tooltip on the Key column header in the detected trackers table. */
export const TRACKERS_TABLE_KEY_HEADER_TOOLTIP =
  'The visible name of this tracker in the browser (cookie name, storage key, or similar).'

/** Scan detail modal / copy-export labels for each tracker field. */
export const SCAN_DETAIL_TRACKER_FIELD_LABELS = {
  vendor: 'Vendor',
  domain: 'Domain',
  name: 'Key',
  type: 'Type',
}

/** Namespaced URL keys for the Results table (shared `/cookies-trackers` route). */
export const TRACKERS_QUERY_KEYS = {
  category: 'category',
  type: 'trackerType',
  search: 'search',
  sortBy: 'trackerSortBy',
  descending: 'trackerDescending',
  page: 'trackerPage',
  rowsPerPage: 'trackerRowsPerPage',
}

/** Sortable Results column `name` values accepted from `trackerSortBy`. */
export const VALID_TRACKERS_SORT_BY = new Set(
  TRACKERS_TABLE_COLUMNS.filter((column) => column.sortable).map((column) => column.name)
)

export const TRACKERS_DEFAULT_SORT = {
  sortBy: TRACKERS_TABLE_COLUMN_NAMES.VENDOR,
  descending: false,
  page: 1,
  rowsPerPage: 10,
}

/** `category: null` means no sidebar category chosen yet for this banner session. */
export const TRACKERS_DEFAULT_FILTER = { search: '', type: null, category: null }

/** Combined filter + sort defaults keyed as URL params for `sanitizeQuery`. */
export const TRACKERS_QUERY_DEFAULTS = {
  [TRACKERS_QUERY_KEYS.category]: TRACKERS_DEFAULT_FILTER.category,
  [TRACKERS_QUERY_KEYS.search]: TRACKERS_DEFAULT_FILTER.search,
  [TRACKERS_QUERY_KEYS.type]: TRACKERS_DEFAULT_FILTER.type,
  [TRACKERS_QUERY_KEYS.sortBy]: TRACKERS_DEFAULT_SORT.sortBy,
  [TRACKERS_QUERY_KEYS.descending]: TRACKERS_DEFAULT_SORT.descending,
  [TRACKERS_QUERY_KEYS.page]: TRACKERS_DEFAULT_SORT.page,
  [TRACKERS_QUERY_KEYS.rowsPerPage]: TRACKERS_DEFAULT_SORT.rowsPerPage,
}

/** Empty-state copy for the trackers summary doughnut card (banner detail). */
export const TRACKERS_SUMMARY_EMPTY_COPY = {
  scanning: 'Scan in progress',
  idle: 'No data available',
}

/** BasicNoData config variants for the detected trackers table (banner detail). */
export const NO_DATA_TRACKERS = {
  empty: {
    icon: 'scan-search',
    title: 'No trackers detected.',
    message: 'Run a scan to detect trackers on your site.',
  },
  scanning: {
    icon: 'radar',
    title: 'Scan in progress.',
    message: 'Trackers will appear here once the scan completes.',
  },
  filtered: {
    icon: 'package-open',
    title: 'No trackers in this category.',
    message: 'Try selecting a different category.',
    clearActionLabel: 'Show all trackers',
  },
  headerFiltered: {
    icon: 'funnel-x',
    title: 'No trackers match the current filters.',
    message: 'Try adjusting your search or filter criteria.',
    clearActionLabel: 'Clear filters',
  },
}

/** BasicNoData config for the scan tab empty state (banner detail). */
export const NO_DATA_SCAN_TAB = {
  empty: {
    icon: 'scan-search',
    title: 'Run a scan to find trackers on your site.',
    message:
      "You'll see results in minutes and can start classifying right away, while we keep scanning in the background for full coverage.",
  },
}

/** BasicNoData config for the scan detail modal trackers column. */
export const NO_DATA_SCAN_DETAIL_TRACKERS = {
  empty: {
    icon: 'cookie',
    title: 'No trackers found.',
    message: 'This scan did not detect any trackers.',
  },
  scanning: {
    icon: 'loader',
    title: 'Scan in progress.',
    message: 'Trackers will appear here once the scan completes.',
  },
}
